import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  type OnDestroy,
  output,
  ViewChild,
} from '@angular/core';

import type { InsightSuggestion } from '@bizzdesign/ai-assistant/workflows';
import { SharedI18nModule } from '@bizzdesign/shared/i18n';
import { TemplateType } from '@bizzdesign/shared/models';
import { SvgDataUriPipe } from '@bizzdesign/shared/pipes';
import { visualizationTemplateRegister } from '@bizzdesign/shared/visualization-templates';
import { TextOverflowTooltip } from '@bizzdesign/ui-components';
import { LoaderComponent } from '@bizzdesign/ui-loader';
import { throttle } from 'lodash-es';
import { type Carousel, CarouselModule } from 'primeng/carousel';
import { TooltipModule } from 'primeng/tooltip';

type VisualInsightSuggestion = InsightSuggestion & {
  chartType: TemplateType;
  previewSvg: string;
};

const visualizationTemplateTypes = new Set<string>(Object.values(TemplateType));

function assertIsVisualizationTemplateType(
  chartType: string,
): asserts chartType is TemplateType {
  if (!visualizationTemplateTypes.has(chartType)) {
    throw new Error(`Unsupported insight suggestion chart type: ${chartType}`);
  }
}
@Component({
  selector: 'bd-ai-assistant-suggestions-v2',
  standalone: true,
  imports: [
    CarouselModule,
    LoaderComponent,
    TooltipModule,
    TextOverflowTooltip,
    SharedI18nModule,
    SvgDataUriPipe,
  ],
  templateUrl: './ai-assistant-suggestions-v2.component.html',
  styleUrls: ['./ai-assistant-suggestions-v2.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AiAssistantSuggestionsV2Component implements OnDestroy {
  refreshing = input(false);
  suggestions = input<InsightSuggestion[]>([]);

  selectedSuggestion = output<InsightSuggestion>();

  protected readonly templateRegister = visualizationTemplateRegister;
  protected readonly visualSuggestions = computed<VisualInsightSuggestion[]>(
    () =>
      this.suggestions().flatMap((suggestion) => {
        try {
          assertIsVisualizationTemplateType(suggestion.chartType);
        } catch (error) {
          console.error(error);
          return [];
        }

        const previewSvg =
          visualizationTemplateRegister[suggestion.chartType]?.previewSvg ?? '';

        const visualSuggestion: VisualInsightSuggestion = {
          ...suggestion,
          chartType: suggestion.chartType,
          previewSvg,
        };

        return [visualSuggestion];
      }),
  );

  @ViewChild('carousel') carousel?: Carousel;

  private readonly onWheelScrollThrottled = throttle(
    (direction: 'forward' | 'backward') => {
      this.navigateCarousel(direction);
    },
    300,
    { leading: true, trailing: false },
  );

  private readonly suggestionSelectCooldownMs = 1500;
  private lastSuggestionSelectAt = 0;

  onCarouselWheel(event: WheelEvent): void {
    const direction = event.deltaY > 0 ? 'forward' : 'backward';

    if (!this.canNavigate(direction)) {
      return;
    }

    event.preventDefault();
    this.onWheelScrollThrottled(direction);
  }

  onCarouselKeydown(event: KeyboardEvent): void {
    if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
      event.preventDefault();
      this.navigateCarousel('forward');
      return;
    }

    if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
      event.preventDefault();
      this.navigateCarousel('backward');
    }
  }

  onSuggestionSpaceKeydown(event: Event, suggestion: InsightSuggestion): void {
    event.preventDefault();
    this.onSuggestionSelect(suggestion);
  }

  onSuggestionSelect(suggestion: InsightSuggestion): void {
    const now = Date.now();
    if (now - this.lastSuggestionSelectAt < this.suggestionSelectCooldownMs) {
      return;
    }

    this.lastSuggestionSelectAt = now;
    this.selectedSuggestion.emit(suggestion);
  }

  ngOnDestroy(): void {
    this.onWheelScrollThrottled.cancel();
  }

  private canNavigate(direction: 'forward' | 'backward'): boolean {
    if (!this.carousel) {
      return false;
    }

    return direction === 'forward'
      ? !this.carousel.isForwardNavDisabled()
      : !this.carousel.isBackwardNavDisabled();
  }

  private navigateCarousel(direction: 'forward' | 'backward'): void {
    if (!this.carousel || !this.canNavigate(direction)) {
      return;
    }

    this.carousel.page =
      direction === 'forward' ? this.carousel.page + 1 : this.carousel.page - 1;
  }
}
